import { ActivityTypeCode, UserLevel } from "project-config/constants";
import { BalanceTipType, ExtraWelfareTipType, RechargeInfo } from "../types";
import { couponTypeTranslate } from "@/components/top-up/help/couponTypeTranslate";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import {
  AnimationVipDesc,
  BalanceWelfareType,
  ExtraWelfareType,
} from "../constants/fox-balance-info";
import {
  findWelfareData,
  presentAmountCalculate,
  rechargeLevel,
} from "@/components/top-up/help/activityUtil";
import { initVerifyRechargeInfo } from "../store";

const t = getTranslateNsT("topup");

export class RechargeTips {
  private rechargeInfo: RechargeInfo = {
    amount: 0,
    userLevel: null,
    userCumulative: 0,
    currencySymbol: "",
    verifyRechargeInfo: initVerifyRechargeInfo,
    HangTags: [],
    activityInfo: {
      id: 0,
      activityType: -1,
      activityName: "",
      activityDetail: [],
      activityNameEn: "",
    },
    luckDrawGiftActivityInfo: {
      id: 0,
      activityType: -1,
      activityName: "",
      activityDetail: [],
      activityNameEn: "",
    },
    lotteryActivityInfoList: [],
    checkedRadioId: 0,
  };
  public balanceTip: BalanceTipType[] = [
    {
      type: BalanceWelfareType.balance,
      content: "",
    },
    {
      type: BalanceWelfareType.UpGradeVip,
      content: "",
      pre: t("animation_upgrade_text"),
    },
  ];

  public extraWelfareTip: ExtraWelfareTipType[] = [
    {
      type: ExtraWelfareType.GPU,
      content: [],
      suf: couponTypeTranslate(ExtraWelfareType.GPU),
      pre: "US$",
    },
    {
      type: ExtraWelfareType.CPU,
      content: [],
      suf: couponTypeTranslate(ExtraWelfareType.CPU),
      pre: "US$",
    },
    {
      type: ExtraWelfareType.Universal,
      content: [],
      suf: couponTypeTranslate(ExtraWelfareType.Universal),
      pre: "US$",
    },
    {
      type: ExtraWelfareType.LuckyDrawGift,
      content: [],
      suf: t("topup_lottery_get_coupon_number"),
    },
  ];

  private balance = this.balanceTip.find(
    (item) => item.type === BalanceWelfareType.balance
  );
  private upgradeVip = this.balanceTip.find(
    (item) => item.type === BalanceWelfareType.UpGradeVip
  );
  private luckyDraw = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.LuckyDrawGift
  );
  private universal = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.Universal
  );

  constructor(rechargeInfo: RechargeInfo) {
    this.rechargeInfo = rechargeInfo;
  }

  public init() {
    this.rechargeAmount();
    this.LuckyDrawGiftTips();
    this.rechargeGiftCouponTips();
    this.rechargeExchange();
  }

  // 余额到账(包含余额+升级会员)
  private rechargeAmount() {
    const {
      amount,
      currencySymbol,
      userLevel,
      activityInfo,
      userCumulative,
      HangTags,
    } = this.rechargeInfo;
    if (
      activityInfo &&
      activityInfo.activityType === ActivityTypeCode.QingCloudGift
    )
      return;
    if (this.balance && amount > 0) {
      this.balance.content = `${currencySymbol}${amount}`;
    }
    if (
      !activityInfo &&
      userLevel &&
      userLevel !== UserLevel.Diamond &&
      HangTags &&
      this.upgradeVip
    ) {
      const riseLevel =
        rechargeLevel(HangTags, false, userCumulative, amount) ||
        UserLevel.Normal;
      if (riseLevel > userLevel) {
        this.upgradeVip.content = `${t(AnimationVipDesc[riseLevel])}`;
      }
    }
  }

  // 抽奖券活动
  private LuckyDrawGiftTips() {
    const { luckDrawGiftActivityInfo, amount, lotteryActivityInfoList } =
      this.rechargeInfo;
    if (
      luckDrawGiftActivityInfo &&
      luckDrawGiftActivityInfo.activityType === ActivityTypeCode.LuckyDrawGift
    ) {
      const { amount: lotteryAmount, lotteryCount } =
        luckDrawGiftActivityInfo.activityDetail[0];
      const lotteryNumber = Math.floor(
        ((amount || 1) / (lotteryAmount || 5000)) * (Number(lotteryCount) || 1)
      );
      const alreadyExistsCoupon = lotteryActivityInfoList
        ? lotteryActivityInfoList.filter((item) => item.payRecordId).length
        : 0;
      if (
        luckDrawGiftActivityInfo.maxCouponAmount &&
        luckDrawGiftActivityInfo.maxCouponAmount - alreadyExistsCoupon &&
        lotteryNumber &&
        this.luckyDraw
      ) {
        this.luckyDraw.content.push(
          lotteryNumber >
            (luckDrawGiftActivityInfo.maxCouponAmount - alreadyExistsCoupon ||
              0)
            ? luckDrawGiftActivityInfo.maxCouponAmount - alreadyExistsCoupon
            : lotteryNumber
        );
      }
    }
  }

  // 充值赠送券活动
  private rechargeGiftCouponTips() {
    const {
      activityInfo,
      amount,
      checkedRadioId,
      userCumulative,
      HangTags,
      userLevel,
    } = this.rechargeInfo;
    if (amount < 1) return;
    if (activityInfo?.activityType === ActivityTypeCode.RechargeGift) {
      const welfareData = findWelfareData(activityInfo, amount || 0);
      if (welfareData) {
        let welfare = welfareData;
        if (welfare.detailList && welfare.detailList.length > 1) {
          welfare =
            welfare.detailList.find((item) => item.id === checkedRadioId) || {};
        }
        const calcWelfareData = presentAmountCalculate(
          activityInfo,
          amount,
          welfare.presentAmount
        );
        const welfareCouponType = welfare.couponType;
        const extra = this.extraWelfareTip.find(
          (item) => item.type === welfareCouponType
        );
        if (extra && calcWelfareData) {
          extra.content.push(calcWelfareData);
        }
      }
      if (
        userLevel &&
        userLevel !== UserLevel.Diamond &&
        HangTags &&
        this.upgradeVip
      ) {
        const riseLevel =
          rechargeLevel(HangTags, false, userCumulative, amount) ||
          UserLevel.Normal;
        if (riseLevel > userLevel) {
          this.upgradeVip.content = `${t(AnimationVipDesc[riseLevel])}`;
        }
      }
    }
  }

  // fox充值兑换
  private rechargeExchange() {
    const { amount, verifyRechargeInfo } = this.rechargeInfo;
    const { couponNO, rechargeAmount, couponAmount, couponType } =
      verifyRechargeInfo;
    const extra = this.extraWelfareTip.find((item) => item.type === couponType);
    if (couponNO && amount >= rechargeAmount && this.universal && extra) {
      extra.content.push(couponAmount);
    }
  }
}
